import React, { useEffect, useState } from 'react'
import { useStyletron } from 'styletron-react'
import { lightTheme } from '@/customTheme'
import { ObjectType } from '@/scenes/engine/common/constants'
import { StaticImage } from 'fabric/fabric-impl'
import { fabric } from 'fabric'
import Icons from '../../../Icons'
import { ENABLE_MULTI_IMAGE_AI_EDITING, MAX_MULTI_IMAGE_COUNT } from '@/constants/aiEditingFlags'
import { hexToRgba } from '@/scenes/engine/utils/utils'
import InfoIcon from '../../../Icons/InfoIcon'
import { useTranslation } from 'react-i18next'

interface ImagePreviewProps {
  activeObject: StaticImage | fabric.ActiveSelection | null
  showCloseOnHover: boolean
  onCloseClick: () => void
  editor?: any // Editor instance to get page preview
  currentSceneId?: string // Current scene ID
  disabled?: boolean // If true, hides the preview while preserving layout
  isOverLayer?: boolean
}

export default function ImagePreview({
  activeObject,
  showCloseOnHover,
  onCloseClick,
  editor,
  currentSceneId,
  disabled = false,
  isOverLayer,
}: ImagePreviewProps) {
  const [css] = useStyletron()
  const [showClose, setShowClose] = useState(false)

  const [t] = useTranslation()

  const onMouseEnter = () => {
    setShowClose(true)
  }

  const onMouseLeave = () => {
    setShowClose(false)
  }
  
  // Determine if preview should be visible based on internal logic
  const shouldShowPreview = !disabled && (
    (activeObject && (
      activeObject.type === ObjectType.BAZAART_IMAGE || 
      (ENABLE_MULTI_IMAGE_AI_EDITING && activeObject.type === ObjectType.ACTIVE_SELECTION)
    )) || 
    (ENABLE_MULTI_IMAGE_AI_EDITING && !activeObject && editor && currentSceneId)
  )

  // Common container styles - using useStyletron for consistency and performance
  const containerStyle = css({
    position: 'relative',
    height: '40px',
    width: 'fit-content',
    borderRadius: '8px',
    opacity: shouldShowPreview ? 1 : 0,
    pointerEvents: shouldShowPreview ? 'auto' : 'none',
  })

  // Common close button styles
  const closeButtonStyle = css({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    width: '16px',
    height: '16px',
    top: '-6px',
    right: '-6px',
    borderRadius: '50%',
    border: `1px solid ${lightTheme.colors.white}`,
    background: lightTheme.colors.grayScale500,
    ':hover': {
      background: lightTheme.colors.grayScale600,
    },
    cursor: 'pointer',
  })

  // Render close button
  const renderCloseButton = () => showCloseOnHover && showClose ? (
    <div className={closeButtonStyle} onClick={onCloseClick}>
      <Icons.SmallCloseIcn />
    </div>
  ) : null


  // Render image from src (string) or Fabric object. Handles both page preview (data URL) and canvas images.
  // Pass withOutline=true for the square overlapping thumbnail style used in multi-image mode.
  const renderImage = (source: StaticImage | string, withOutline = false) => {
    const src = typeof source === 'string' ? source : (() => {
      const obj = source
      const cp = obj.clipPath
      obj.clipPath = null
      const dataUrl = obj.toDataURL({})
      obj.clipPath = cp
      return dataUrl
    })()

    if (withOutline) {
      return (
        <div
          style={{
            width: '40px',
            height: '40px',
            outline: '2px solid white',
            borderRadius: '8px',
            backgroundColor: '#f2f2f2',
            overflow: 'hidden',
            flexShrink: 0,
            boxSizing: 'border-box',
          }}
        >
          <img
            src={src}
            alt=""
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      )
    }

    return (
      <div
        style={{
          backgroundImage: `url(${src})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minWidth: '24px',
          maxWidth: '72px',
          width: 'auto',
          height: '100%',
          boxShadow: `inset 0 0 0 1px ${hexToRgba(lightTheme.colors.black, 0.1)}`,
          borderRadius: '8px',
        }}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          style={{ height: '40px', width: 'auto', borderRadius: '8px', objectFit: 'cover', opacity: 0 }}
        />
      </div>
    )
  }

  // Render page preview (null activeObject case) - only if flag enabled
  // Scene.preview is a string (data URL), not a Fabric object
  const renderPagePreview = () => {
    if (!ENABLE_MULTI_IMAGE_AI_EDITING || !editor || !currentSceneId) return null

    const currentScene = editor.handlers.pagesHandler.scenes.find(scene => scene.sceneId === currentSceneId)
    const pagePreview = currentScene?.preview

    if (!pagePreview || typeof pagePreview !== 'string') return null

    return <>
      {renderImage(pagePreview)}
      {renderCloseButton()}
    </>
  }

  // Render multiple images
  const renderMultipleImages = (activeSelection: fabric.ActiveSelection) => {
    const imageObjects = activeSelection.getObjects().filter(obj => obj.type === ObjectType.BAZAART_IMAGE) as unknown[] as StaticImage[]
    const visibleImages = imageObjects.slice(0, MAX_MULTI_IMAGE_COUNT)

    return (
      <div style={{ display: 'flex', alignItems: 'center', paddingRight: '15px' }}>
        {visibleImages.map((obj, index) => (
          <div
            key={(obj as any).cacheKey || (obj as any).layerAssetStateId || index}
            style={{ marginRight: '-15px', zIndex: visibleImages.length + index }}
          >
            {renderImage(obj, true)}
          </div>
        ))}
      </div>
    )
  }

  // Handle null activeObject case (show page preview)
  if (!activeObject) {
    return renderPagePreview()
  }

  if (isOverLayer) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flex: 1,
          height: '100%',
        }}
      >
        <InfoIcon color={lightTheme.colors.primary} style={{ flexShrink: 0 }} />
        <span
          style={{
            fontSize: '14px',
            color: lightTheme.colors.primary,
            whiteSpace: 'nowrap',
          }}
        >
          {t('You can edit with AI up to {{count}} photos.', { count: MAX_MULTI_IMAGE_COUNT })}
        </span>
      </div>
    )
  }

  // Handle invalid activeObject types
  if (activeObject.type !== ObjectType.BAZAART_IMAGE && 
      (!ENABLE_MULTI_IMAGE_AI_EDITING || activeObject.type !== ObjectType.ACTIVE_SELECTION)) {
    return null
  }

  // Render based on activeObject type
  return (
    <div className={containerStyle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {activeObject.type === ObjectType.BAZAART_IMAGE ? (
        renderImage((activeObject as StaticImage))
      ) : (ENABLE_MULTI_IMAGE_AI_EDITING && activeObject.type === ObjectType.ACTIVE_SELECTION) ? (
        renderMultipleImages(activeObject as fabric.ActiveSelection)
      ) : null}
      {renderCloseButton()}
    </div>
  )
}